/** Create-plan form validators (used by the form and tests). */

export { validateCreditRange, type CreditRangeValidation } from '@/utils/validate-credit-range';

export interface PlanNameValidation {
  isValid: boolean;
  errorMessage?: string;
}

/** Non-empty trimmed name. */
export function validatePlanName(name: string | null | undefined): PlanNameValidation {
  const trimmed = (name ?? '').trim();
  if (trimmed.length === 0) {
    return { isValid: false, errorMessage: 'Plan name is required.' };
  }
  return { isValid: true };
}

/** Non-empty trimmed requirement name (same rules as plan name). */
export function validateRequirementName(name: string | null | undefined): PlanNameValidation {
  const trimmed = (name ?? '').trim();
  if (trimmed.length === 0) {
    return { isValid: false, errorMessage: 'Requirement name is required.' };
  }
  return { isValid: true };
}

/** Invariant locale: credits entry/sanitizers only allow `.` as decimal separator, not `,`. */
const requirementCreditsDisplayFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  useGrouping: false,
});

/**
 * Display string for requirement credits in read-only UI, aligned with
 * `NeonNumberField` `dataMaxDecimals={2}` on the editable requirement form.
 * Uses `en-US` so the decimal separator is always `.`, matching
 * {@link sanitizeCreditsInput} and avoiding locale-dependent tests.
 */
export function formatRequirementCreditsForDisplay(value: number): string {
  return requirementCreditsDisplayFormatter.format(value);
}

/** Required credits from NeonNumberField `modelValue` (finite, non-negative). */
export function validateRequirementCreditsValue(
  value: number | undefined | null
): PlanNameValidation {
  if (value === undefined || value === null) {
    return { isValid: false, errorMessage: 'Credits are required.' };
  }
  if (!Number.isFinite(value) || value < 0) {
    return { isValid: false, errorMessage: 'Enter a valid non-negative number.' };
  }
  return { isValid: true };
}

/** Required credits from string (e.g. tests, legacy text input). */
export function validateRequirementCredits(value: string | null | undefined): PlanNameValidation {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0 || trimmed === '.') {
    return { isValid: false, errorMessage: 'Credits are required.' };
  }
  return validateRequirementCreditsValue(parseFloat(trimmed));
}

/**
 * Neon `customValidationFunction` is typed with a string value, but `NeonNumberField` may pass a
 * number at runtime. Branch so we never call string-only helpers on a number.
 */
export function validateRequirementCreditsField(
  value: string | number | null | undefined
): PlanNameValidation {
  if (typeof value === 'number') {
    return validateRequirementCreditsValue(value);
  }
  return validateRequirementCredits(value);
}

/** Years before / after graduating class for dropdown bounds. */
export const CLASS_YEAR_PAST_YEARS = 6;
export const CLASS_YEAR_FUTURE_YEARS = 12;

/** Graduating “current year” rolls forward after this calendar date. */
const ROLL_OVER_MONTH = 7;
const ROLL_OVER_DAY = 25;

/** Graduating class year from rollover date (see `ROLL_OVER_*`). */
export function getCurrentGraduatingClassYear(): number {
  const today = new Date();
  const year = today.getFullYear();
  const semesterLimit = new Date(year, ROLL_OVER_MONTH - 1, ROLL_OVER_DAY);
  return today < semesterLimit ? year : year + 1;
}

/** Min/max class years for dropdowns from `getCurrentGraduatingClassYear()`. */
export function getClassYearRange(): { min: number; max: number } {
  const currentYear = getCurrentGraduatingClassYear();
  return {
    min: currentYear - CLASS_YEAR_PAST_YEARS,
    max: currentYear + CLASS_YEAR_FUTURE_YEARS,
  };
}

/** "Class of {year}" options for each year in range. */
export function getClassYearOptions(): Array<{ value: string; text: string }> {
  const { min, max } = getClassYearRange();
  const options: Array<{ value: string; text: string }> = [];
  for (let y = min; y <= max; y++) {
    options.push({ value: String(y), text: `Class of ${y}` });
  }
  return options;
}

/** “To” years from selected “from” through range max. */
export function getClassYearToOptions(
  classYearFrom: number | null | undefined
): Array<{ value: string; text: string }> {
  const { min, max } = getClassYearRange();
  const from = classYearFrom ?? min;
  const options: Array<{ value: string; text: string }> = [];
  for (let y = from; y <= max; y++) {
    options.push({ value: String(y), text: `Class of ${y}` });
  }
  return options;
}

export interface ClassYearValidation {
  isValid: boolean;
  errorMessage?: string;
}

/** Year string in range; optional floor for the “to” field. */
export function validateClassYear(
  value: string | null | undefined,
  minYear?: number | null
): ClassYearValidation {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    return { isValid: false, errorMessage: 'Class year is required.' };
  }
  const num = parseInt(trimmed, 10);
  if (Number.isNaN(num) || !Number.isInteger(Number(trimmed))) {
    return { isValid: false, errorMessage: 'Enter a valid year.' };
  }
  const { min, max } = getClassYearRange();
  if (num < min || num > max) {
    return {
      isValid: false,
      errorMessage: `Year must be between ${min} and ${max}.`,
    };
  }
  if (minYear !== null && minYear !== undefined && num < minYear) {
    return {
      isValid: false,
      errorMessage: 'To year must be the same or after from year.',
    };
  }
  return { isValid: true };
}
